import { add, isAfter, isBefore, lastDayOfMonth, setDay } from "date-fns";

import { usHolidays as US_HOLIDAY_CATALOG } from "./constants";
import {
  getYardCalendarDateParts,
  getYardCalendarDayKey,
} from "./dateUtils";

/** Yard schedule calendar day string for dedupe / fast lookup. */
export const calendarDayKey = getYardCalendarDayKey;

/**
 * Application-level encoding for `Schedule.holidays: string[]` (no GraphQL nested type).
 *
 * - Preset (US catalog): plain catalog key, e.g. `Christmas`
 * - Custom one-time: `custom_once|YYYY-MM-DD|Display name` (name may contain `|`)
 * - Custom yearly: `custom_yearly|MM-DD|Display name`
 */

/** Canonical holiday rule kinds used in forms and in-memory (not persisted as objects). */
export const HOLIDAY_RULE_KIND = {
  PRESET_RECURRING: "presetRecurring",
  CUSTOM_ONE_TIME: "customOneTime",
  CUSTOM_RECURRING: "customRecurring",
};

const PREFIX_CUSTOM_ONCE = "custom_once|";
const PREFIX_CUSTOM_YEARLY = "custom_yearly|";

/** @param {unknown} value */
export const isHolidayRule = (value) =>
  value &&
  typeof value === "object" &&
  typeof value.kind === "string" &&
  value.kind.length > 0;

const stripTypename = (rule) => {
  if (!rule || typeof rule !== "object") {
    return rule;
  }
  const { __typename, ...rest } = rule;
  return rest;
};

/** @param {unknown} v */
const asTrimmedString = (v) =>
  typeof v === "string" ? v.trim() : "";

const isValidIsoCalendarDate = (value) => {
  const match =
    typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return false;
  }

  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const isValidRecurringMonthDay = (month, day) => {
  if (
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1
  ) {
    return false;
  }

  const date = new Date(Date.UTC(2000, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/**
 * Legacy / API preset rows often have `name` (or `holidayKey`) but no `presetKey`;
 * the Holiday SelectInput is bound to `presetKey`, so those rows look "empty" after load.
 * @param {unknown[]} rules
 * @param {Record<string, string>} catalog
 */
export const normalizePresetHolidayRules = (rules, catalog) => {
  if (!Array.isArray(rules)) {
    return rules;
  }
  const cat = catalog || {};
  return rules.map((raw) => {
    const rule = stripTypename(raw);
    if (!isHolidayRule(rule) || rule.kind !== HOLIDAY_RULE_KIND.PRESET_RECURRING) {
      return rule;
    }

    const legacyKey = asTrimmedString(
      rule.holidayKey ?? rule.holiday ?? rule.key,
    );
    const presetKeyRaw = asTrimmedString(rule.presetKey);
    const nameRaw = asTrimmedString(rule.name);

    if (presetKeyRaw && cat[presetKeyRaw]) {
      return { ...rule, presetKey: presetKeyRaw, name: nameRaw || presetKeyRaw };
    }

    for (const candidate of [legacyKey, presetKeyRaw, nameRaw].filter(Boolean)) {
      if (cat[candidate]) {
        return { ...rule, presetKey: candidate, name: nameRaw || candidate };
      }
    }

    const lower = (s) => s.toLowerCase();
    const candidates = [presetKeyRaw, legacyKey, nameRaw].filter(Boolean);
    for (const candidate of candidates) {
      const match = Object.keys(cat).find((k) => lower(k) === lower(candidate));
      if (match) {
        return { ...rule, presetKey: match, name: nameRaw || match };
      }
    }

    return {
      ...rule,
      presetKey: presetKeyRaw || undefined,
      name: nameRaw || undefined,
    };
  });
};

const toIsoDate = (value) => {
  if (!value) {
    return null;
  }
  if (isValidIsoCalendarDate(value)) {
    return value;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return calendarDayKey(value);
  }
  return null;
};

const isEmptyHolidayRuleRow = (rule) =>
  !rule ||
  typeof rule !== "object" ||
  (!rule.kind &&
    !rule.name &&
    !rule.presetKey &&
    !rule.date &&
    !rule.month &&
    !rule.day);

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Decode one stored holiday string into an in-memory rule, or null if unknown/malformed.
 * @param {string} str
 * @param {Record<string, string>} usHolidaysCatalog
 */
export const decodeHolidayString = (str, usHolidaysCatalog) => {
  if (typeof str !== "string") {
    return null;
  }
  const trimmed = str.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith(PREFIX_CUSTOM_ONCE)) {
    const body = trimmed.slice(PREFIX_CUSTOM_ONCE.length);
    const dateMatch = body.match(/^(\d{4}-\d{2}-\d{2})\|(.*)$/s);
    if (!dateMatch) {
      return null;
    }
    const [, date, nameRaw] = dateMatch;
    const name = (nameRaw || "").trim();
    if (!name) {
      return null;
    }
    return {
      kind: HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME,
      name,
      date,
    };
  }

  if (trimmed.startsWith(PREFIX_CUSTOM_YEARLY)) {
    const body = trimmed.slice(PREFIX_CUSTOM_YEARLY.length);
    const mdMatch = body.match(/^(\d{2}-\d{2})\|(.*)$/s);
    if (!mdMatch) {
      return null;
    }
    const [, mmdd, nameRaw] = mdMatch;
    if (!/^\d{2}-\d{2}$/.test(mmdd)) {
      return null;
    }
    const [mm, dd] = mmdd.split("-").map((x) => Number(x));
    if (
      Number.isNaN(mm) ||
      Number.isNaN(dd) ||
      mm < 1 ||
      mm > 12 ||
      dd < 1 ||
      !isValidRecurringMonthDay(mm, dd)
    ) {
      return null;
    }
    const name = (nameRaw || "").trim();
    if (!name) {
      return null;
    }
    return {
      kind: HOLIDAY_RULE_KIND.CUSTOM_RECURRING,
      name,
      month: mm,
      day: dd,
    };
  }

  const catalogEntry = usHolidaysCatalog?.[trimmed];
  if (
    typeof catalogEntry === "string" &&
    catalogEntry.trim().length > 0
  ) {
    return {
      kind: HOLIDAY_RULE_KIND.PRESET_RECURRING,
      name: trimmed,
      presetKey: trimmed,
    };
  }

  return null;
};

/**
 * Encode an in-memory rule to a persistence string, or null if invalid for storage.
 * @param {object} rawRule
 */
export const encodeHolidayRule = (rawRule) => {
  const rule = stripTypename(rawRule);
  if (!isHolidayRule(rule)) {
    return null;
  }

  if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING) {
    const key = rule.presetKey;
    if (!key || typeof key !== "string") {
      return null;
    }
    return key;
  }

  if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME) {
    const name = rule.name?.trim();
    const date = toIsoDate(rule.date);
    if (!name || !date) {
      return null;
    }
    return `${PREFIX_CUSTOM_ONCE}${date}|${name}`;
  }

  if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_RECURRING) {
    const name = rule.name?.trim();
    const month = Number(rule.month);
    const day = Number(rule.day);
    if (
      !name ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      !isValidRecurringMonthDay(month, day)
    ) {
      return null;
    }
    const mmdd = `${pad2(month)}-${pad2(day)}`;
    return `${PREFIX_CUSTOM_YEARLY}${mmdd}|${name}`;
  }

  return null;
};

/**
 * @param {object} rule
 * @param {Set<string>} seenPreset
 * @param {unknown[]} out
 */
const pushRuleDedupingPresets = (rule, seenPreset, out) => {
  if (
    rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING &&
    rule.presetKey &&
    seenPreset.has(rule.presetKey)
  ) {
    return;
  }
  if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING && rule.presetKey) {
    seenPreset.add(rule.presetKey);
  }
  out.push(rule);
};

/**
 * Decode stored `string[]` (and tolerate legacy in-memory rule objects) into UI rule objects.
 * @param {unknown[]} entries
 * @param {Record<string, string>} usHolidaysCatalog
 */
export const holidayStringsToRules = (entries, usHolidaysCatalog) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }
  const catalog = usHolidaysCatalog || {};
  const hasString = entries.some((entry) => typeof entry === "string");
  const hasObject = entries.some((entry) => isHolidayRule(entry));
  if (!hasString && hasObject) {
    return normalizePresetHolidayRules(
      entries.filter(isHolidayRule).map((r) => stripTypename(r)),
      catalog,
    );
  }

  const out = [];
  const seenPreset = new Set();
  entries.forEach((entry) => {
    if (typeof entry === "string") {
      const rule = decodeHolidayString(entry, catalog);
      if (!rule) {
        return;
      }
      pushRuleDedupingPresets(rule, seenPreset, out);
      return;
    }
    if (isHolidayRule(entry)) {
      pushRuleDedupingPresets(stripTypename(entry), seenPreset, out);
    }
  });
  return normalizePresetHolidayRules(out, catalog);
};

/**
 * Serialize validated UI rules to `string[]` for GraphQL (dedupes presets).
 * @param {unknown[]} rules
 * @param {Record<string, string>} [catalog]
 */
export const rulesToHolidayStrings = (
  rules,
  catalog = US_HOLIDAY_CATALOG,
) => {
  if (!Array.isArray(rules)) {
    return [];
  }
  const cat = catalog || {};
  const hasString = rules.some((e) => typeof e === "string");
  const normalized = hasString
    ? holidayStringsToRules(rules, cat)
    : normalizePresetHolidayRules(
        rules.filter(isHolidayRule).map((r) => stripTypename(r)),
        cat,
      );
  const seenPreset = new Set();
  const out = [];
  normalized.forEach((raw) => {
    const rule = stripTypename(raw);
    if (!isHolidayRule(rule)) {
      return;
    }
    if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING) {
      if (!rule.presetKey || seenPreset.has(rule.presetKey)) {
        return;
      }
      seenPreset.add(rule.presetKey);
    }
    const encoded = encodeHolidayRule(rule);
    if (encoded) {
      out.push(encoded);
    }
  });
  return out;
};

/**
 * React-final-form ArrayInput requires a real Array. Some API/cache paths can
 * produce list-shaped plain objects such as { "0": ..., "1": ... }.
 * @param {unknown} raw
 * @returns {unknown[]}
 */
export const coerceFormArrayField = (raw) => {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return [...raw];
  }
  if (typeof raw === "object") {
    const numericKeys = Object.keys(raw).filter((key) => /^\d+$/.test(key));
    if (numericKeys.length > 0) {
      return numericKeys
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => raw[key]);
    }
  }
  return [];
};

/**
 * Schedule-shaped object with `holidays` replaced by decoded in-memory rules (forms).
 * @param {object|undefined|null} schedule
 * @param {Record<string, string>} catalog
 */
export const decodeHolidaysInSchedule = (schedule, catalog) => {
  const base = schedule || {};
  const holidays = coerceFormArrayField(base.holidays);
  const workdays = coerceFormArrayField(base.workdays);
  return {
    ...base,
    workdays,
    holidays: holidayStringsToRules(holidays, catalog || {}),
  };
};

/**
 * Shallow-clone `schedule` or use empty defaults, then decode persisted holiday
 * strings into in-memory rules for event-type create/edit forms. Fresh
 * `holidays`/`workdays` arrays come from the coercion in
 * {@link decodeHolidaysInSchedule}, which also tolerates list-shaped objects.
 * For edit flows where `schedule` is missing, call {@link decodeHolidaysInSchedule} instead
 * so defaults are not injected for a null/undefined API value.
 * @param {object|undefined|null} schedule
 * @param {Record<string, string>} catalog
 */
export const initialDecodedScheduleForEventTypeForm = (schedule, catalog) => {
  const base =
    schedule && typeof schedule === "object"
      ? { ...schedule }
      : {
          workdays: [],
          holidays: [],
          minNoticeHours: 24,
          maxDaysIntoFuture: 60,
        };
  return decodeHolidaysInSchedule(base, catalog || {});
};

/**
 * Shallow-clones `data.schedule` and replaces `holidays` with encoded `string[]` for GraphQL.
 * @param {{ schedule?: { holidays?: unknown[] } }} data
 */
export const encodeScheduleHolidaysForMutation = (data) => {
  if (!data?.schedule) {
    return;
  }
  data.schedule = { ...data.schedule };
  data.schedule.holidays = rulesToHolidayStrings(
    coerceFormArrayField(data.schedule.holidays),
  );
};

const getHolidayDateFromParts = (year, month, week, dayOfWeek) => {
  let date;
  if (week < 0) {
    date = lastDayOfMonth(new Date(year, month, 1));
    date = setDay(date, dayOfWeek);
    if (date.getMonth() > month) {
      date = add(date, { days: -7 });
    }
  } else {
    date = new Date(year, month, 1);
    date = setDay(date, dayOfWeek);
    if (year > date.getFullYear() || month > date.getMonth()) {
      week += 1;
    }
    date = add(date, { days: week * 7 });
  }
  return date;
};

/**
 * @param {string[]} dList result of `definition.split(",")`
 * @param {number} holidayYear
 */
const holidayDateFromPresetParts = (dList, holidayYear) => {
  const month = Number(dList[0]);
  if (dList.length === 2) {
    return new Date(holidayYear, month, Number(dList[1]));
  }
  return getHolidayDateFromParts(
    holidayYear,
    month,
    Number(dList[1]),
    Number(dList[2])
  );
};

/**
 * @param {object} rule
 * @param {Record<string, string>} usHolidaysCatalog
 * @param {number} year
 */
const expandPresetRuleForYear = (rule, usHolidaysCatalog, year) => {
  const dates = [];
  const key = rule.presetKey;
  if (!key || !usHolidaysCatalog[key]) {
    return dates;
  }
  const definition = usHolidaysCatalog[key];
  if (typeof definition !== "string" || !definition.trim()) {
    return dates;
  }
  if (key === "Day after Thanksgiving") {
    // The catalog formula for this preset ("4th Friday") lands on the wrong day
    // in years where the 4th Friday precedes the 4th Thursday, so derive it as
    // Thanksgiving + 1. Self-contained: blocks the day even when only this
    // preset is selected (not just when "Thanksgiving Day" is also selected).
    const thanksgivingDef = usHolidaysCatalog["Thanksgiving Day"];
    if (typeof thanksgivingDef === "string" && thanksgivingDef.trim()) {
      const thanksgiving = holidayDateFromPresetParts(
        thanksgivingDef.split(","),
        year
      );
      dates.push(add(thanksgiving, { days: 1 }));
    }
    return dates;
  }
  const dList = definition.split(",");
  if (dList.length !== 2 && dList.length !== 3) {
    return dates;
  }
  dates.push(holidayDateFromPresetParts(dList, year));
  return dates;
};

/**
 * @param {object} rule
 * @param {number} year
 */
const expandCustomRecurringForYear = (rule, year) => {
  const month = rule.month;
  const day = rule.day;
  if (
    typeof month !== "number" ||
    typeof day !== "number" ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    !isValidRecurringMonthDay(month, day)
  ) {
    return [];
  }
  return [new Date(year, month - 1, day)];
};

/**
 * @param {object} rule
 */
const expandCustomOneTime = (rule) => {
  const iso = toIsoDate(rule.date);
  if (!iso) {
    return [];
  }
  const [y, m, d] = iso.split("-").map(Number);
  return [new Date(y, m - 1, d)];
};

/**
 * Computes blocked calendar dates from holiday strings / rules within [windowStart, windowEnd].
 * @param {unknown[]} holidays
 * @param {Date} windowStart
 * @param {Date} windowEnd
 * @param {Record<string, string>} usHolidaysCatalog
 */
export const resolveHolidayRulesToBlockedDates = (
  holidays,
  windowStart,
  windowEnd,
  usHolidaysCatalog
) => {
  const catalog = usHolidaysCatalog || {};
  const rules = holidayStringsToRules(
    coerceFormArrayField(holidays),
    catalog,
  );

  const y0 = getYardCalendarDateParts(windowStart).year;
  const y1 = getYardCalendarDateParts(windowEnd).year;
  const collected = [];

  rules.forEach((rule) => {
    if (!isHolidayRule(rule)) {
      return;
    }
    if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME) {
      expandCustomOneTime(rule).forEach((dt) => {
        if (!isBefore(dt, windowStart) && !isAfter(dt, windowEnd)) {
          collected.push(dt);
        }
      });
      return;
    }
    for (let year = y0; year <= y1; year += 1) {
      let chunk = [];
      if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING) {
        chunk = expandPresetRuleForYear(rule, catalog, year);
      } else if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_RECURRING) {
        chunk = expandCustomRecurringForYear(rule, year);
      }
      chunk.forEach((dt) => {
        if (!isBefore(dt, windowStart) && !isAfter(dt, windowEnd)) {
          collected.push(dt);
        }
      });
    }
  });

  const seenDayKeys = new Set();
  const deduped = [];
  collected.forEach((dt) => {
    const key = calendarDayKey(dt);
    if (seenDayKeys.has(key)) {
      return;
    }
    seenDayKeys.add(key);
    deduped.push(dt);
  });
  return deduped;
};

/**
 * @param {unknown} rules
 * @returns {string|undefined}
 */
export const validateScheduleHolidayRules = (rules) => {
  const list = normalizePresetHolidayRules(
    coerceFormArrayField(rules),
    US_HOLIDAY_CATALOG,
  );
  if (list.length === 0) {
    return undefined;
  }
  if (list.every(isEmptyHolidayRuleRow)) {
    return undefined;
  }
  const presetKeys = new Set();
  for (let i = 0; i < list.length; i += 1) {
    const rule = list[i];
    if (isEmptyHolidayRuleRow(rule)) {
      continue;
    }
    if (!isHolidayRule(rule)) {
      return `Holiday row ${i + 1} is invalid`;
    }
    if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING) {
      if (!rule.presetKey) {
        return `Preset holiday row ${i + 1}: choose a holiday`;
      }
      if (presetKeys.has(rule.presetKey)) {
        return `Duplicate preset holiday: ${rule.presetKey}`;
      }
      presetKeys.add(rule.presetKey);
    } else if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME) {
      if (!rule.name?.trim()) {
        return `Custom one-time row ${i + 1}: name is required`;
      }
      const iso = toIsoDate(rule.date);
      if (!iso) {
        return `Custom one-time row ${i + 1}: date must be YYYY-MM-DD`;
      }
    } else if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_RECURRING) {
      if (!rule.name?.trim()) {
        return `Custom recurring row ${i + 1}: name is required`;
      }
      const month = Number(rule.month);
      const day = Number(rule.day);
      if (
        Number.isNaN(month) ||
        Number.isNaN(day) ||
        month < 1 ||
        month > 12 ||
        day < 1
      ) {
        return `Custom recurring row ${i + 1}: month (1-12) and day (1-31) are required`;
      }
      if (!isValidRecurringMonthDay(month, day)) {
        return `Custom recurring row ${i + 1}: date must exist for the selected month`;
      }
    } else {
      return `Holiday row ${i + 1}: unknown kind`;
    }
  }
  return undefined;
};
